import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';
import Translate from '@prisjakt/ui-translate';
import { StarIcon } from '@prisjakt/ui-icons';
import RatingHelper from './rating-helper';

import * as S from './styles';

class Rating extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedRating: 0,
    };
  }

  renderInteractive(stars) {
    // Uses a css technique to fills every star on the left side of selected radio button, in this labels
    // are floated to the right. To emit correct values i.e. first star emits 1 and so forth,
    // the map of rate values is reversed.
    return (
      <S.Rating>
        <S.Fieldset>
          {stars.reverse().map(rate => [
            <input
              name="rating"
              id={`rate-input-${rate}`}
              key={`rate-input-${rate}`}
              checked={
                this.state.selectedRating > 0
                  ? rate === this.state.selectedRating
                  : rate === RatingHelper.toWhole(this.props.rate)
              }
              onChange={e => {
                if (this.props.onChange) {
                  this.props.onChange(e);
                }
                this.setState({ selectedRating: rate });
              }}
              value={rate * 2} // back to range of 1..10
              type="radio"
            />,
            // eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control
            <label key={`rate-label-${rate}`} htmlFor={`rate-input-${rate}`}>
              <StarIcon height={this.props.size === 'medium' ? 16 : 12} />
            </label>,
          ])}
        </S.Fieldset>
      </S.Rating>
    );
  }

  static shouldShowRate(showRate, rating) {
    return showRate && rating.whole > 0;
  }

  renderRate(rating) {
    const { size, showRate, showRateAs, rateTranslation } = this.props;

    if (!Rating.shouldShowRate(showRate, rating)) {
      return null;
    }

    return showRateAs === 'text' ? (
      <S.RateText size={size}>
        <Translate domain={rateTranslation.domain} n={rating.rounded}>
          {rateTranslation.key}
        </Translate>
      </S.RateText>
    ) : (
      <S.RateNumber size={size}>{rating.rounded}</S.RateNumber>
    );
  }

  render() {
    const {
      link,
      canRate,
      size,
      hide,
      count,
      nrOfDecimals,
      onChange,
      singleStar,
      centerAlign,
      showCounter,
      showCounterAs,
      counterPosition,
      secondaryColors,
      countTranslation,
      className,
    } = this.props;

    if (hide) {
      return null;
    }

    const { t } = this.context;
    const starCount = singleStar ? 1 : 5;
    const stars = new Array(starCount).fill().map((_, i) => i + 1);

    const rating = RatingHelper.convert(this.props.rate, nrOfDecimals);

    const halfStar = rating.half !== rating.whole;

    const starSize = size === 'medium' ? 16 : 12;
    const renderStars = (
      <S.StarIcons
        secondaryColors={secondaryColors}
        halfStar={halfStar}
        stars={stars}
        rating={rating}
        size={starSize}
      />
    );

    const renderCounter = showCounter && count > 0 && (
      <S.Counter
        interactive={onChange || link}
        size={size}
        counterPosition={counterPosition}
      >
        {showCounterAs === 'text' ? (
          <Translate domain={countTranslation.domain} n={count}>
            {countTranslation.key}
          </Translate>
        ) : (
          `(${count})`
        )}
      </S.Counter>
    );

    const renderRate = this.renderRate(rating);

    if (canRate) {
      return this.renderInteractive(stars);
    }

    if (link) {
      return (
        <S.Rating href={link}>
          {renderStars}
          {renderCounter}
          {renderRate}
        </S.Rating>
      );
    }

    return (
      <S.Rating
        centerAlign={centerAlign}
        className={className}
        aria-label={t('reviews', 'rating.rate-n-text-version', {
          n: rating.rounded,
        })}
      >
        <S.StarsWrapper counterPosition={counterPosition}>
          {renderStars}
          {renderRate}
        </S.StarsWrapper>
        {renderCounter}
      </S.Rating>
    );
  }
}

Rating.contextType = AppContext;

const { number, bool, oneOf, func, string, shape } = PropTypes;

Rating.propTypes = {
  rate: number,
  count: number,
  hide: bool,
  nrOfDecimals: number,
  singleStar: bool,
  showCounter: bool,
  showCounterAs: oneOf(['number', 'text']),
  showRate: bool,
  showRateAs: oneOf(['number', 'text']),
  centerAlign: bool,
  secondaryColors: bool,
  size: oneOf(['medium', 'small']),
  counterPosition: oneOf(['right', 'bottom']),
  countTranslation: shape({ domain: string, key: string }),
  rateTranslation: shape({ domain: string, key: string }),
  link: string,
  canRate: bool,
  onChange: func,
  className: string,
};

Rating.defaultProps = {
  rate: 0,
  count: 0,
  hide: false,
  nrOfDecimals: 1,
  singleStar: false,
  showCounter: true,
  showCounterAs: 'text',
  showRate: false,
  showRateAs: 'number',
  centerAlign: false,
  secondaryColors: false,
  size: 'medium',
  counterPosition: 'right',
  countTranslation: {
    domain: 'reviews',
    key: 'rating.n-reviews',
  },
  rateTranslation: {
    domain: 'reviews',
    key: 'rating.rate-n-text-version.md',
  },
  link: null,
  canRate: false,
  onChange: undefined,
  className: '',
};

export default Rating;
